import type { ReactNode } from 'react'

type FieldTone = 'success' | 'warning' | 'danger' | 'neutral'

export interface FieldResponseOption<T extends string | number> {
  value: T
  label: string
  description?: string
  tone: FieldTone
}

const toneClasses: Record<FieldTone, { idle: string; selected: string; dot: string }> = {
  success: {
    idle: 'border-slate-200 bg-white text-emerald-700 hover:border-emerald-300',
    selected: 'border-emerald-600 bg-emerald-50 text-emerald-800 shadow-sm',
    dot: 'bg-emerald-600',
  },
  warning: {
    idle: 'border-slate-200 bg-white text-amber-700 hover:border-amber-300',
    selected: 'border-amber-500 bg-amber-50 text-amber-800 shadow-sm',
    dot: 'bg-amber-500',
  },
  danger: {
    idle: 'border-slate-200 bg-white text-rose-700 hover:border-rose-300',
    selected: 'border-rose-600 bg-rose-50 text-rose-800 shadow-sm',
    dot: 'bg-rose-600',
  },
  neutral: {
    idle: 'border-slate-200 bg-white text-slate-600 hover:border-slate-300',
    selected: 'border-slate-500 bg-slate-100 text-slate-800 shadow-sm',
    dot: 'bg-slate-500',
  },
}

export function FieldProgressStrip({
  value,
  label,
}: {
  value: number
  label: string
}) {
  const boundedValue = Math.max(0, Math.min(100, value))
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs font-bold text-slate-500">
        <span>{label}</span>
        <span>{boundedValue}%</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-lg bg-slate-200">
        <div
          className="h-full rounded-lg bg-gradient-to-r from-teal-600 via-sky-600 to-amber-500 transition-all duration-300"
          style={{ width: `${boundedValue}%` }}
        />
      </div>
    </div>
  )
}

export function FieldPanel({
  eyebrow,
  title,
  meta,
  children,
}: {
  eyebrow?: string
  title: string
  meta?: ReactNode
  children?: ReactNode
}) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {eyebrow ? <p className="text-[11px] font-bold uppercase text-teal-700">{eyebrow}</p> : null}
          <h2 className="mt-1 text-base font-black leading-snug text-slate-950">{title}</h2>
        </div>
        {meta ? <div className="shrink-0">{meta}</div> : null}
      </div>
      {children ? <div className="mt-4">{children}</div> : null}
    </section>
  )
}

export function FieldResponseGroup<T extends string | number>({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: T | undefined
  options: Array<FieldResponseOption<T>>
  onChange: (value: T) => void
}) {
  return (
    <div className="space-y-2">
      <p className="text-xs font-bold uppercase text-slate-500">{label}</p>
      <div className="grid grid-cols-3 gap-2">
        {options.map((option) => {
          const selected = option.value === value
          const tone = toneClasses[option.tone]
          return (
            <button
              key={String(option.value)}
              type="button"
              onClick={() => onChange(option.value)}
              aria-pressed={selected}
              className={[
                'min-h-16 rounded-lg border px-2 py-2 text-center transition',
                'focus:outline-none focus:ring-2 focus:ring-sky-200',
                selected ? tone.selected : tone.idle,
              ].join(' ')}
            >
              <span className="mx-auto mb-1 flex items-center justify-center gap-1 text-sm font-black">
                <span className={`h-2 w-2 rounded-full ${tone.dot}`} />
                {option.label}
              </span>
              {option.description ? (
                <span className="block text-[10px] font-semibold leading-tight text-slate-500">
                  {option.description}
                </span>
              ) : null}
            </button>
          )
        })}
      </div>
    </div>
  )
}
